import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { EditorView, keymap, lineNumbers } from "@codemirror/view"
import { EditorState } from "@codemirror/state"
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands"
import { searchKeymap, highlightSelectionMatches } from "@codemirror/search"
import { autocompletion, completionKeymap } from "@codemirror/autocomplete"
import {
  bracketMatching,
  codeFolding,
  foldEffect,
  foldKeymap,
  unfoldEffect,
} from "@codemirror/language"
import { markdown } from "@codemirror/lang-markdown"
import { getFrontmatterFoldRange } from "@/lib/markdown-frontmatter"
import { oneDark } from "@codemirror/theme-one-dark"
import { findLineRangeForExcerpt } from "@/lib/markdown-line-range"
import { setGfmTaskAtIndex } from "@/lib/gfm-task-toggle"
import { applyBasecampDraftStatus } from "@/lib/basecamp-draft-fence"
import { TipTapEditor } from "./tiptap-editor"
import type { ViewportFocusCoords } from "./tiptap-editor.utils"
import { MarkdownPreview } from "./markdown-preview"
import { PreviewFindBar } from "@/components/features/preview-find-bar"
import { usePreviewFind } from "@/hooks/use-preview-find"
import { DiffOverviewRuler } from "@/components/features/diff-overview-ruler"
import { buildChangedLinesOverviewMarkers } from "@/lib/diff-overview-markers"
import {
  bindReadingPositionPersistence,
  restoreReadingPosition,
} from "@/lib/document-reading-position"
import {
  captureScrollRatioFromElement,
  restoreScrollRatioOnElement,
} from "@/lib/document-scroll-restore"
import { devLog } from "@/lib/dev-log"
import { testIds } from "@/lib/test-ids"
import { changeGutter, updateChangedLinesEffect, getEmptyChangedLines, type ChangedLinesState } from "@/lib/codemirror-extensions/change-gutter"

interface MarkdownEditorProps {
  initialContent?: string
  onChange?: (content: string, options?: { immediate?: boolean }) => void
  readOnly?: boolean
  darkMode?: boolean
  className?: string
  onInternalLinkClick?: (href: string, event?: MouseEvent) => void
  viewMode?: 'wysiwyg' | 'source'
  changedLines?: ChangedLinesState | null
  showChangeMarkers?: boolean
  documentId?: string // Required for image uploads and copy-to-chip
  documentTitle?: string // Human-readable title for the chip (e.g. "my-note.md")
  documentPath?: string // Relative file path for the chip tooltip
  documentType?: string | null
  documentFrontmatter?: Record<string, unknown> | null
  onCopyWithReference?: (data: { text: string; lineStart?: number; lineEnd?: number; isFullLine: boolean }) => void
  onFocus?: () => void
  onBlur?: () => void
  /** When true, the leading YAML `---` block is folded so the body is visible; line numbers still match the file. */
  collapseFrontmatter?: boolean
  /**
   * Controlled: whether TipTap should be mounted (true = edit mode).
   * When omitted, the editor manages this internally (always mounts TipTap).
   */
  wysiwygEditorMounted?: boolean
  /** Called when the user double-clicks the preview to request edit mode. */
  onActivateWysiwyg?: () => void
  /** One-shot: focus TipTap after mount (e.g. newly created document). */
  wysiwygAutoFocus?: boolean
  /** Clears parent one-shot auto-focus after TipTap applies it. */
  onWysiwygAutoFocusComplete?: () => void
  /** Reading column vs full-width (matches document viewer status bar toggle). */
  pageWidthMode?: "default" | "full"
  /** Ref populated with flush fn while TipTap is mounted (sync HTML to markdown). */
  wysiwygFlushRef?: React.MutableRefObject<(() => string) | null>
}

function isEditableField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable
  )
}

export function MarkdownEditor({
  initialContent = "",
  onChange,
  readOnly = false,
  darkMode = false,
  className = "",
  onInternalLinkClick,
  viewMode = 'wysiwyg',
  changedLines = null,
  showChangeMarkers = false,
  documentId,
  documentTitle,
  documentPath,
  documentType,
  documentFrontmatter = null,
  onCopyWithReference,
  onFocus,
  onBlur,
  collapseFrontmatter = false,
  wysiwygEditorMounted: wysiwygEditorMountedProp,
  onActivateWysiwyg,
  wysiwygAutoFocus = false,
  onWysiwygAutoFocusComplete,
  pageWidthMode = "default",
  wysiwygFlushRef,
}: MarkdownEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)
  const collapseFrontmatterRef = useRef(collapseFrontmatter)
  collapseFrontmatterRef.current = collapseFrontmatter
  const [_isReady, setIsReady] = useState(false)
  const [content, setContent] = useState(initialContent)
  const lastEmittedContent = useRef(initialContent)
  // Ref for the shared scroll container (preview + edit share one scroller)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  // Preserve scroll across preview <-> TipTap swap (and if focus tries to scrollIntoView).
  const pendingScrollTopRef = useRef<number | null>(null)
  const prevWysiwygMountedRef = useRef(false)
  // Approx scroll ratio across Visual <-> Source (different scrollers / content heights).
  const lastScrollRatioRef = useRef(0)
  const pendingModeScrollRatioRef = useRef<number | null>(null)
  const suppressScrollTrackRef = useRef(false)
  const prevViewModeRef = useRef(viewMode)
  // CodeMirror scroll DOM for change overview ruler (source + change markers)
  const cmScrollRef = useRef<HTMLElement | null>(null)
  const [cmScrollEl, setCmScrollEl] = useState<HTMLElement | null>(null)
  // Always-current ref so the copy handler reads fresh content without being in its deps
  const contentRef = useRef(content)
  contentRef.current = content
  const [pendingFocusCoords, setPendingFocusCoords] = useState<ViewportFocusCoords | null>(null)
  const [pendingFocusScrollTop, setPendingFocusScrollTop] = useState<number | null>(null)
  /** Preview DOM root for Cmd+F find-without-edit (Visual preview / read-only). */
  const previewFindRootRef = useRef<HTMLDivElement>(null)

  const changeOverviewMarkers = useMemo(() => {
    if (viewMode !== 'source' || !showChangeMarkers || !changedLines) return []
    const totalLines = Math.max(content.split('\n').length, 1)
    return buildChangedLinesOverviewMarkers(changedLines, totalLines)
  }, [viewMode, showChangeMarkers, changedLines, content])

  const captureScrollTop = () => {
    const el = scrollContainerRef.current
    if (!el) return null
    const top = el.scrollTop
    pendingScrollTopRef.current = top
    return top
  }

  const restorePendingScrollTop = () => {
    const el = scrollContainerRef.current
    const top = pendingScrollTopRef.current
    if (!el || top == null) return
    el.scrollTop = top
  }

  const handlePreviewDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target instanceof HTMLInputElement && event.target.type === "checkbox") {
      return
    }
    event.preventDefault()
    const top = captureScrollTop()
    setPendingFocusScrollTop(top)
    const coords = { clientX: event.clientX, clientY: event.clientY }
    setPendingFocusCoords(coords)
    onActivateWysiwyg?.()
  }

  const handleWysiwygAutoFocusComplete = () => {
    restorePendingScrollTop()
    setPendingFocusCoords(null)
    setPendingFocusScrollTop(null)
    // Keep pendingScrollTopRef until the restore rAF loop finishes (focus runs async).
    onWysiwygAutoFocusComplete?.()
  }

  // When the prop is provided, use it; otherwise fall back to always-active (legacy).
  const wysiwygEditorMounted = wysiwygEditorMountedProp ?? true
  const previewMaxWidth = pageWidthMode === "full" ? "none" : "760px"

  // Find in Visual preview / read-only without mounting TipTap (avoids accidental edit).
  const previewFindEnabled =
    viewMode === 'wysiwyg' && (readOnly || !wysiwygEditorMounted)

  const {
    open: previewFindOpen,
    query: previewFindQuery,
    current: previewFindCurrent,
    total: previewFindTotal,
    openFind: openPreviewFind,
    close: closePreviewFind,
    setFindQuery: setPreviewFindQuery,
    findNext: previewFindNext,
    findPrev: previewFindPrev,
    focusRequestId: previewFindFocusRequestId,
  } = usePreviewFind({
    containerRef: previewFindRootRef,
    contentKey: previewFindEnabled ? `${documentId ?? 'doc'}:${content.length}` : null,
    enabled: previewFindEnabled,
  })

  useEffect(() => {
    if (!previewFindEnabled) return

    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'f' || e.shiftKey || e.altKey) {
        return
      }
      const target = e.target as HTMLElement | null
      const inFindBar = Boolean(
        target?.closest(`[data-testid="${testIds.documentPreviewFind.bar}"]`),
      )
      if (!inFindBar && isEditableField(e.target)) return
      if (!rootRef.current || rootRef.current.getClientRects().length === 0) return

      e.preventDefault()
      e.stopPropagation()
      openPreviewFind()
    }

    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [previewFindEnabled, openPreviewFind])

  useEffect(() => {
    if (wysiwygEditorMounted) {
      closePreviewFind()
    }
  }, [wysiwygEditorMounted, closePreviewFind])

  useEffect(() => {
    if (wysiwygEditorMounted) return
    setPendingFocusCoords(null)
    setPendingFocusScrollTop(null)
  }, [wysiwygEditorMounted, documentId])

  // Latch cross-mode ratio before paint so reading-position restore can skip.
  useLayoutEffect(() => {
    if (prevViewModeRef.current === viewMode) return
    pendingModeScrollRatioRef.current = lastScrollRatioRef.current
    prevViewModeRef.current = viewMode
  }, [viewMode])

  // Persist reading position while scrolling; restore after document identity changes.
  // Skip session restore when returning from Source - mode-toggle ratio wins.
  useEffect(() => {
    if (!documentId || viewMode !== 'wysiwyg') return
    const root = scrollContainerRef.current
    if (!root) return
    const unbind = bindReadingPositionPersistence(documentId, root)
    if (pendingModeScrollRatioRef.current == null) {
      const restored = restoreReadingPosition(documentId, root)
      if (!restored) {
        // First paint may precede layout; retry briefly
        requestAnimationFrame(() => restoreReadingPosition(documentId, root))
      }
    }
    return unbind
  }, [documentId, viewMode, wysiwygEditorMounted])

  // Track scroll ratio on the active scroller so mode toggles can restore approximately.
  useEffect(() => {
    if (viewMode !== 'wysiwyg') return
    const el = scrollContainerRef.current
    if (!el) return
    const sync = () => {
      if (suppressScrollTrackRef.current) return
      const ratio = captureScrollRatioFromElement(el)
      if (ratio != null) lastScrollRatioRef.current = ratio
    }
    sync()
    el.addEventListener('scroll', sync, { passive: true })
    return () => {
      sync()
      el.removeEventListener('scroll', sync)
    }
  }, [viewMode, wysiwygEditorMounted])

  useEffect(() => {
    if (viewMode !== 'source' || !cmScrollEl) return
    const sync = () => {
      if (suppressScrollTrackRef.current) return
      const ratio = captureScrollRatioFromElement(cmScrollEl)
      if (ratio != null) lastScrollRatioRef.current = ratio
    }
    sync()
    cmScrollEl.addEventListener('scroll', sync, { passive: true })
    return () => {
      sync()
      cmScrollEl.removeEventListener('scroll', sync)
    }
  }, [viewMode, cmScrollEl])

  // Rough scroll restore when toggling Visual <-> Source (ratio, not pixel-perfect).
  useEffect(() => {
    const ratio = pendingModeScrollRatioRef.current
    if (ratio == null) return

    const target =
      viewMode === 'source' ? cmScrollEl : scrollContainerRef.current
    if (!target) return

    suppressScrollTrackRef.current = true
    restoreScrollRatioOnElement(target, ratio)

    let frames = 0
    const maxFrames = 24
    const tick = () => {
      restoreScrollRatioOnElement(target, ratio)
      frames += 1
      if (frames < maxFrames) {
        requestAnimationFrame(tick)
      } else {
        pendingModeScrollRatioRef.current = null
        suppressScrollTrackRef.current = false
        const settled = captureScrollRatioFromElement(target)
        if (settled != null) lastScrollRatioRef.current = settled
      }
    }
    requestAnimationFrame(tick)
  }, [viewMode, cmScrollEl, wysiwygEditorMounted])

  // Capture scroll before preview->edit (e.g. Edit button) and restore after TipTap mounts.
  // Double-click also captures in the handler; this covers the status-bar Edit path.
  useEffect(() => {
    const wasMounted = prevWysiwygMountedRef.current
    prevWysiwygMountedRef.current = wysiwygEditorMounted

    if (!wasMounted && wysiwygEditorMounted) {
      if (pendingScrollTopRef.current == null) {
        const top = captureScrollTop()
        if (top != null) setPendingFocusScrollTop(top)
      }
      restorePendingScrollTop()
      let frames = 0
      const maxFrames = 12
      const tick = () => {
        restorePendingScrollTop()
        frames += 1
        if (frames < maxFrames) {
          requestAnimationFrame(tick)
        } else {
          pendingScrollTopRef.current = null
        }
      }
      requestAnimationFrame(tick)
      return
    }

    if (wasMounted && !wysiwygEditorMounted) {
      // Leaving edit: keep current scroll on the shared container (stable key).
      pendingScrollTopRef.current = null
    }
  }, [wysiwygEditorMounted])

  // Handle content changes
  const handleChange = (newContent: string, options?: { immediate?: boolean; syncOnly?: boolean }) => {
    setContent(newContent)
    lastEmittedContent.current = newContent
    onChange?.(newContent, options)
  }

  const handlePreviewTaskToggle = (index: number, checked: boolean) => {
    if (readOnly) return
    const next = setGfmTaskAtIndex(contentRef.current, index, checked)
    if (next === null || next === contentRef.current) return
    handleChange(next)
  }

  const handleBasecampDraftStatus = (fenceSource: string, status: string) => {
    if (readOnly) return
    const next = applyBasecampDraftStatus(contentRef.current, fenceSource, status)
    if (next === null || next === contentRef.current) return
    handleChange(next)
  }


  useEffect(() => {
    // Only create CodeMirror when in source mode
    if (viewMode !== 'source') return
    if (!editorRef.current) return

    // Defensive: guard against a previous EditorView's DOM surviving an unmount race (e.g. the
    // cleanup below firing after this setup runs). Without this, a second EditorView could be
    // appended alongside a stale one, producing two independently-scrolling source views.
    if (editorRef.current.firstChild) {
      editorRef.current.replaceChildren()
    }

    // Build extensions array carefully
    const extensions = [
      history(),
      codeFolding(),
      bracketMatching(),
      autocompletion(),
      highlightSelectionMatches(),
      markdown(),
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        ...searchKeymap,
        ...completionKeymap,
        ...foldKeymap,
      ]),
      EditorState.readOnly.of(readOnly),
      EditorView.lineWrapping,
      EditorView.theme({
        "&": {
          height: "100%",
          fontSize: "14px",
        },
        ".cm-scroller": {
          fontFamily: "ui-monospace, SFMono-Regular, Monaco, monospace",
          lineHeight: "1.6",
        },
        ".cm-content": {
          padding: "16px 0",
        },
        ".cm-line": {
          padding: "0 16px",
        },
      }),
    ]

    // Add line numbers (always enabled)
    extensions.push(lineNumbers())

    // Add change tracking gutter support
    extensions.push(...changeGutter())

    // Note: Copy handler will be added after view is created (see below)

    // Add onChange listener if provided
    if (onChange) {
      extensions.push(
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            const newContent = update.state.doc.toString()
            // Only update if content actually changed (avoid loops)
            // Also check if we're in the middle of a programmatic update
            const currentView = viewRef.current
            const isUpdating = currentView ? (currentView as any)._isUpdating : false
            const isEmitting = currentView ? (currentView as any)._isEmitting : false
            if (newContent !== lastEmittedContent.current && !isUpdating && !isEmitting) {
              // Mark as emitting to prevent feedback loop
              if (currentView) {
                (currentView as any)._isEmitting = true
              }
              setContent(newContent)
              lastEmittedContent.current = newContent
              onChange(newContent)
              // Clear flag after a microtask
              Promise.resolve().then(() => {
                if (viewRef.current) {
                  (viewRef.current as any)._isEmitting = false
                }
              })
            }
          }
        })
      )
    }

    // Add dark theme if requested
    if (darkMode) {
      extensions.push(oneDark)
    }

    // Use initialContent directly to ensure we start with the correct content
    // content state might be stale on first render
    const docContent = initialContent ?? content ?? ""
    
    // Sync content state with what we're putting in the editor
    if (docContent !== content) {
      setContent(docContent)
      lastEmittedContent.current = docContent
    }
    
    const startState = EditorState.create({
      doc: docContent,
      extensions,
    })

    const view = new EditorView({
      state: startState,
      parent: editorRef.current,
    })

    // Initialize update flags
    ;(view as any)._isUpdating = false
    ;(view as any)._isEmitting = false

    // Add focus/blur listeners
    if (onFocus || onBlur) {
      const focusListener = () => {
        onFocus?.()
      }
      const blurListener = () => {
        onBlur?.()
      }
      view.dom.addEventListener('focus', focusListener)
      view.dom.addEventListener('blur', blurListener)
      
      // Store listeners for cleanup
      ;(view as any)._focusListener = focusListener
      ;(view as any)._blurListener = blurListener
    }

    viewRef.current = view
    cmScrollRef.current = view.scrollDOM
    setCmScrollEl(view.scrollDOM)
    setIsReady(true)

    // Add copy handler after view is created whenever documentId is known.
    // The onCopyWithReference callback is optional — clipboard data is always set
    // so that ContentEditableChatInput can detect the paste even without the callback.
    if (documentId) {
      const copyHandler = (event: ClipboardEvent) => {
        const selection = view.state.selection.main
        if (selection.empty) return

        const doc = view.state.doc
        const startLine = doc.lineAt(selection.from)
        const endLine = doc.lineAt(selection.to)
        
        // Check if this is a multi-line selection
        const isMultiLine = startLine.number !== endLine.number
        
        // For multi-line selections, "round up" to full lines
        // Expand selection to include full lines if it's close to the edges
        let expandedFrom = selection.from
        let expandedTo = selection.to
        let shouldCreateChip = false
        
        if (isMultiLine) {
          // Multi-line selection: always expand to full lines (round up)
          // This makes it easier to create chips from multi-line selections
          expandedFrom = startLine.from
          expandedTo = endLine.to
          shouldCreateChip = true
        } else {
          // Single line selection: must be exact full line
          const selectionStartsAtLineStart = selection.from === startLine.from
          const selectionEndsAtLineEnd = selection.to >= endLine.to
          shouldCreateChip = selectionStartsAtLineStart && selectionEndsAtLineEnd
        }
        
        // Get the expanded text
        const expandedText = doc.sliceString(expandedFrom, expandedTo)
        const finalStartLine = doc.lineAt(expandedFrom)
        const finalEndLine = doc.lineAt(expandedTo)
        
        if (shouldCreateChip) {
          const lineStart = finalStartLine.number
          const lineEnd = finalEndLine.number
          
          // Set custom clipboard data with expanded selection
          const clipboardData = {
            type: 'document-reference',
            documentId: documentId,
            filePath: documentPath || documentId,
            title: documentTitle || documentPath?.split('/').pop() || documentId.split('/').pop() || documentId,
            documentType: 'note',
            lineStart: lineStart,
            lineEnd: lineEnd,
            text: expandedText
          }
          
          // Set clipboard data — must preventDefault or the browser's default copy
          // overwrites the clipboard and drops application/json (breaks paste-to-chat chips).
          if (event.clipboardData) {
            event.preventDefault()
            event.clipboardData.setData('text/plain', expandedText)
            event.clipboardData.setData('application/json', JSON.stringify(clipboardData))
            devLog('[MarkdownEditor] Copy with reference (expanded):', { 
              original: { from: selection.from, to: selection.to },
              expanded: { from: expandedFrom, to: expandedTo },
              lineStart, 
              lineEnd, 
              documentId 
            })
          }
          
          // Also call callback (if provided)
          onCopyWithReference?.({
            text: expandedText,
            lineStart: lineStart,
            lineEnd: lineEnd,
            isFullLine: true
          })
        } else {
          // Partial selection - just text, no custom data
          const originalText = doc.sliceString(selection.from, selection.to)
          onCopyWithReference?.({
            text: originalText,
            isFullLine: false
          })
        }
      }
      
      view.dom.addEventListener('copy', copyHandler as EventListener)
      
      // Also expand selection on mouseup for multi-line selections
      const mouseUpHandler = (_event: MouseEvent) => {
        const selection = view.state.selection.main
        if (selection.empty) return
        
        const doc = view.state.doc
        const startLine = doc.lineAt(selection.from)
        const endLine = doc.lineAt(selection.to)
        
        // Only expand multi-line selections - always expand to full lines
        const isMultiLine = startLine.number !== endLine.number
        if (!isMultiLine) return
        
        // Always expand multi-line selections to full lines
        const expandedFrom = startLine.from
        const expandedTo = endLine.to
        
        // Only expand if selection isn't already at full lines
        if (selection.from !== expandedFrom || selection.to !== expandedTo) {
          view.dispatch({
            selection: {
              anchor: expandedFrom,
              head: expandedTo
            }
          })
          devLog('[MarkdownEditor] Expanded selection on mouseup:', {
            original: { from: selection.from, to: selection.to },
            expanded: { from: expandedFrom, to: expandedTo }
          })
        }
      }
      
      view.dom.addEventListener('mouseup', mouseUpHandler as EventListener)
      
      // Store handlers for cleanup
      ;(view as any)._copyHandler = copyHandler
      ;(view as any)._mouseUpHandler = mouseUpHandler
    }

    return () => {
      // Remove focus/blur listeners
      if ((view as any)._focusListener) {
        view.dom.removeEventListener('focus', (view as any)._focusListener)
      }
      if ((view as any)._blurListener) {
        view.dom.removeEventListener('blur', (view as any)._blurListener)
      }
      // Remove copy handler
      if ((view as any)._copyHandler) {
        view.dom.removeEventListener('copy', (view as any)._copyHandler)
      }
      // Remove mouseup handler
      if ((view as any)._mouseUpHandler) {
        view.dom.removeEventListener('mouseup', (view as any)._mouseUpHandler)
      }
      view.destroy()
      viewRef.current = null
      setCmScrollEl(null)
      cmScrollRef.current = null
      setIsReady(false)
    }
    // onChange is intentionally not in deps to avoid recreation loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, darkMode, viewMode])

  useEffect(() => {
    if (viewMode !== 'source') return
    const view = viewRef.current
    if (!view) return

    const payload = showChangeMarkers && changedLines
      ? changedLines
      : getEmptyChangedLines()

    view.dispatch({
      effects: updateChangedLinesEffect.of({
        added: payload.added ?? [],
        removed: payload.removed ?? [],
        modified: payload.modified ?? [],
      }),
    })
  }, [changedLines, showChangeMarkers, viewMode])

  const applyFrontmatterFoldState = (view: EditorView, collapse: boolean) => {
    const r = getFrontmatterFoldRange(view.state.doc.toString())
    if (!r) return
    view.dispatch({ effects: collapse ? foldEffect.of(r) : unfoldEffect.of(r) })
  }

  // Update content when initialContent changes from an external source
  useEffect(() => {
    // Only update if initialContent is different from what we last emitted
    // This prevents feedback loops from our own onChange causing cursor jumping
    if (initialContent !== lastEmittedContent.current && initialContent !== content) {
      // Only update if the new content is actually different
      const shouldUpdate = initialContent !== content
      if (shouldUpdate) {
        setContent(initialContent)
        lastEmittedContent.current = initialContent
        
        // Update CodeMirror editor if it exists and content changed
        if (viewMode === 'source' && viewRef.current) {
          const view = viewRef.current
          const currentDoc = view.state.doc.toString()
          if (currentDoc !== initialContent) {
            // Mark as updating to prevent feedback loop
            ;(view as any)._isUpdating = true
            view.dispatch({
              changes: {
                from: 0,
                to: view.state.doc.length,
                insert: initialContent
              }
            })
            // Clear the flag after a short delay
            setTimeout(() => {
              ;(view as any)._isUpdating = false
            }, 0)
            requestAnimationFrame(() => {
              const v = viewRef.current
              if (v) applyFrontmatterFoldState(v, collapseFrontmatterRef.current)
            })
          }
        }
      }
    }
  }, [initialContent, viewMode, content])

  // Update CodeMirror editor when content state changes (but not from user edits)
  useEffect(() => {
    if (viewMode !== 'source') return
    const view = viewRef.current
    if (!view) return
    
    const currentDoc = view.state.doc.toString()
    // Only update if content changed externally (not from user typing)
    // We check if it matches lastEmittedContent to avoid feedback loops
    // Also check that content is different from current doc to avoid unnecessary updates
    const isEmitting = (view as any)._isEmitting
    if (content !== currentDoc && content === lastEmittedContent.current && !isEmitting) {
      // Prevent infinite loops by checking if we're already updating
      const isUpdating = (view as any)._isUpdating
      if (!isUpdating) {
        (view as any)._isUpdating = true
        view.dispatch({
          changes: {
            from: 0,
            to: view.state.doc.length,
            insert: content
          }
        })
        // Clear the flag after a short delay
        setTimeout(() => {
          if (viewRef.current) {
            (viewRef.current as any)._isUpdating = false
          }
        }, 0)
      }
    }
  }, [content, viewMode])

  // Fold or unfold the YAML block when the toggle changes (or when entering Source).
  // Placed after other effects so the CodeMirror view from the init effect above exists.
  useEffect(() => {
    if (viewMode !== "source") return
    const view = viewRef.current
    if (!view) return
    applyFrontmatterFoldState(view, collapseFrontmatter)
  }, [collapseFrontmatter, viewMode])

  // Preview-mode copy handler: sets application/json clipboard data so paste-to-chip works
  // even when TipTap isn't mounted yet. Reads content via contentRef to avoid stale closures.
  useEffect(() => {
    if (!documentId) return
    if (wysiwygEditorMounted) return  // TipTap's own copy handler takes over once mounted
    if (viewMode !== 'wysiwyg') return  // CodeMirror (source mode) has its own handler
    const wrapper = scrollContainerRef.current
    if (!wrapper) return

    const handlePreviewCopy = (event: ClipboardEvent) => {
      const selection = window.getSelection()
      if (!selection || selection.isCollapsed) return
      const selectedText = selection.toString()
      if (!selectedText.trim()) return

      // Same locator as the hover "add to chat" bubble, so a pasted chip and a bubble chip for the
      // same passage carry the same line range (tolerates bold, lists, multi-paragraph selections).
      const { lineStart, lineEnd } = findLineRangeForExcerpt(contentRef.current, selectedText)

      const clipboardPayload = {
        type: 'document-reference',
        documentId,
        filePath: documentPath || documentId,
        title: documentTitle || documentPath?.split('/').pop() || documentId.split('/').pop() || documentId,
        documentType: 'note',
        lineStart,
        lineEnd,
        text: selectedText,
      }

      if (event.clipboardData) {
        event.preventDefault()
        event.clipboardData.setData('text/plain', selectedText)
        event.clipboardData.setData('application/json', JSON.stringify(clipboardPayload))
        devLog('[MarkdownEditor] Preview copy with reference:', { lineStart, lineEnd, documentId })
      }

      onCopyWithReference?.({
        text: selectedText,
        lineStart,
        lineEnd,
        isFullLine: lineStart !== undefined,
      })
    }

    wrapper.addEventListener('copy', handlePreviewCopy as EventListener)
    return () => wrapper.removeEventListener('copy', handlePreviewCopy as EventListener)
  }, [documentId, documentPath, documentTitle, wysiwygEditorMounted, viewMode, onCopyWithReference])

  return (
    <div ref={rootRef} className={`h-full flex flex-col ${className}`}>
      <div className="flex-1 min-h-0 overflow-hidden relative">
        {/* Outside the scrollport so the bar stays pinned while matches scroll */}
        {previewFindOpen && previewFindEnabled && (
          <PreviewFindBar
            query={previewFindQuery}
            current={previewFindCurrent}
            total={previewFindTotal}
            onQueryChange={setPreviewFindQuery}
            onNext={previewFindNext}
            onPrev={previewFindPrev}
            onClose={closePreviewFind}
            focusRequestId={previewFindFocusRequestId}
          />
        )}
        {viewMode === 'wysiwyg' ? (
          readOnly ? (
            <div key="wysiwyg-readonly" className="absolute inset-0 overflow-auto print-visible" data-scroll-container="true" data-testid={testIds.markdownEditor.readOnlyPreview}>
              <div ref={previewFindRootRef}>
                <MarkdownPreview
                  content={content}
                  onInternalLinkClick={onInternalLinkClick}
                  documentId={documentId}
                  documentTitle={documentTitle}
                  documentPath={documentPath}
                  documentType={documentType}
                  documentFrontmatter={documentFrontmatter}
                  className="min-h-full"
                  maxWidth={previewMaxWidth}
                  enableReadingMargin
                />
              </div>
            </div>
          ) : (
            <div
              // Stable key for preview + edit so the shared scroller keeps scrollTop when TipTap
              // mounts. Distinct from `source` / `wysiwyg-readonly` so Visual<->Source still
              // fully remounts (avoids stacked CodeMirror/TipTap DOM from Jul 2026 fix).
              key="wysiwyg"
              ref={scrollContainerRef}
              className={`absolute inset-0 overflow-auto print-visible ${!wysiwygEditorMounted ? 'cursor-text' : ''}`}
              data-scroll-container="true"
              data-testid={
                wysiwygEditorMounted
                  ? testIds.markdownEditor.wysiwygEdit
                  : testIds.markdownEditor.wysiwygPreview
              }
              onDoubleClick={!wysiwygEditorMounted ? handlePreviewDoubleClick : undefined}
              role="presentation"
            >
              {wysiwygEditorMounted ? (
                <TipTapEditor
                  initialContent={content}
                  onChange={handleChange}
                  readOnly={readOnly}
                  darkMode={darkMode}
                  onInternalLinkClick={onInternalLinkClick}
                  documentId={documentId}
                  documentTitle={documentTitle}
                  documentPath={documentPath}
                  onCopyWithReference={onCopyWithReference}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  pageWidthMode={pageWidthMode}
                  scrollContainer="external"
                  wysiwygFlushRef={wysiwygFlushRef}
                  autoFocusOnMount={wysiwygAutoFocus || pendingFocusCoords != null}
                  focusCoords={pendingFocusCoords}
                  focusPreserveScrollTop={pendingFocusScrollTop}
                  onAutoFocusComplete={handleWysiwygAutoFocusComplete}
                />
              ) : (
                <div ref={previewFindRootRef}>
                  <MarkdownPreview
                    content={content}
                    onInternalLinkClick={onInternalLinkClick}
                    documentId={documentId}
                    documentTitle={documentTitle}
                    documentPath={documentPath}
                    documentType={documentType}
                    documentFrontmatter={documentFrontmatter}
                    className="min-h-full"
                    maxWidth={previewMaxWidth}
                    enableReadingMargin
                    onToggleTask={handlePreviewTaskToggle}
                    onBasecampDraftStatus={readOnly ? undefined : handleBasecampDraftStatus}
                  />
                </div>
              )}
            </div>
          )
        ) : (
          <div key="source" className="absolute inset-0 overflow-hidden print-visible">
            <div
              ref={editorRef}
              className={`h-full w-full min-h-0 print-visible${
                changeOverviewMarkers.length > 0 ? ' cm-editor-with-overview-ruler' : ''
              }`}
            />
            {cmScrollEl && changeOverviewMarkers.length > 0 ? (
              <DiffOverviewRuler scrollRef={cmScrollRef} markers={changeOverviewMarkers} />
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

